export const normalizeIsFriend = (value, fallback = 0) => {
    if (value === undefined || value === null) return fallback;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
};

export const getContactUserId = (contact = {}) =>
    contact.user_id ||
    contact.friend_user_id ||
    contact.related_user_id ||
    contact.from_user_id ||
    null;

export const extractContactFromSocketPayload = (payload) => {
    if (!payload || typeof payload !== "object") return null;

    const candidates = [
        payload?.contact,
        payload?.data?.contact,
        payload?.data,
        payload?.conversation,
        payload,
    ].filter(Boolean);

    for (const candidate of candidates) {
        if (typeof candidate !== "object" || Array.isArray(candidate)) continue;
        const userId = getContactUserId(candidate);
        if (!userId) continue;
        return { ...candidate, user_id: userId };
    }

    return null;
};

export const mergeContactRecord = (previousContact, incomingContact) => {
    if (!incomingContact) return previousContact || null;
    if (!previousContact) return { ...incomingContact };

    const hasExplicitIsFriend =
        incomingContact.is_friend !== undefined && incomingContact.is_friend !== null;
    const hasExplicitRequestSend =
        incomingContact.request_send !== undefined && incomingContact.request_send !== null;

    const merged = { ...previousContact, ...incomingContact };
    const previousIsFriend = normalizeIsFriend(previousContact.is_friend, 0);

    if (!hasExplicitIsFriend && previousIsFriend === 1) {
        merged.is_friend = previousContact.is_friend;
    } else if (hasExplicitIsFriend) {
        merged.is_friend = incomingContact.is_friend;
    }

    const resolvedIsFriend = normalizeIsFriend(merged.is_friend, 0);

    if (resolvedIsFriend === 1) {
        merged.request_send = hasExplicitRequestSend ? incomingContact.request_send : 0;
        merged.pending_relation_selection =
            incomingContact.pending_relation_selection !== undefined &&
            incomingContact.pending_relation_selection !== null
                ? incomingContact.pending_relation_selection
                : 0;
    } else if (resolvedIsFriend === 0 && hasExplicitIsFriend) {
        merged.request_send = hasExplicitRequestSend ? incomingContact.request_send : 0;
    } else if (hasExplicitRequestSend) {
        merged.request_send = incomingContact.request_send;
    } else {
        const prevRequestSend = Number(previousContact.request_send || 0);
        const incomingRequestSend = Number(incomingContact.request_send || 0);
        if (prevRequestSend > incomingRequestSend) {
            merged.request_send = previousContact.request_send;
        }
    }

    if (resolvedIsFriend !== 1) {
        if (
            Number(previousContact.pending_relation_selection || 0) >
            Number(incomingContact.pending_relation_selection || 0)
        ) {
            merged.pending_relation_selection = previousContact.pending_relation_selection;
        }
    }

    return merged;
};

export const applyContactListPatch = (contacts, patch, { moveToTop = false } = {}) => {
    const list = Array.isArray(contacts) ? contacts : [];
    const userId = getContactUserId(patch);
    if (!userId) return list;

    const index = list.findIndex((c) => String(c.user_id) === String(userId));
    const updated = mergeContactRecord(index >= 0 ? list[index] : null, {
        ...(index >= 0 ? list[index] : {}),
        ...patch,
        user_id: userId,
    });

    if (index >= 0) {
        const rest = list.filter((_, i) => i !== index);
        return moveToTop ? [updated, ...rest] : [...list.slice(0, index), updated, ...list.slice(index + 1)];
    }

    return moveToTop ? [updated, ...list] : [...list, updated];
};

export const CONTACT_SYNC_SOCKET_EVENTS = [
    "contacts_list_updated",
    "contact_updated",
    "friend_request_accepted",
    "friend_request_updated",
    "friend_status_updated",
    "friend_removed",
    "unfriend",
];

export const isContactSyncEventName = (eventName) => {
    const name = String(eventName || "");
    if (CONTACT_SYNC_SOCKET_EVENTS.includes(name)) return true;
    if (name === "chat_list_updated") return false;
    return /friend|contacts_list|contact_updated|unfriend/i.test(name);
};

export const shouldTriggerContactRefresh = (eventName) => {
    const name = String(eventName || "");
    if (name === "chat_list_updated") return false;
    if (CONTACT_SYNC_SOCKET_EVENTS.includes(name)) return true;
    return /notification|friend|contact|relation|unfriend|accept|reject|removed|select_relation/i.test(name);
};

export const CONTACTS_CHANGED_EVENT = "phonebook:contacts-changed";

export const dispatchContactsChanged = (detail = {}) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(CONTACTS_CHANGED_EVENT, { detail }));
};

export const shouldSendReciprocalFriendRequest = (contactsList, receivedRequestsList) => {
    const contacts = Array.isArray(contactsList) ? contactsList : [];
    const receivedRequests = Array.isArray(receivedRequestsList) ? receivedRequestsList : [];

    if (contacts.length !== 1 || receivedRequests.length !== 1) {
        return false;
    }

    const contactUserId = getContactUserId(contacts[0]);
    const requestUserId = getContactUserId(receivedRequests[0]);

    if (!contactUserId || !requestUserId) return false;
    return String(contactUserId) === String(requestUserId);
};

export const sendFriendRequestWithRelations = async (token, userId, relations) => {
    const relationsValue = Array.isArray(relations)
        ? relations.filter(Boolean).join(",")
        : String(relations || "").trim();
    if (!token || !userId || !relationsValue) {
        return { ok: false, message: "Missing friend request data" };
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("relations", relationsValue);

    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/friend-request/send`, {
        method: "POST",
        body: formData,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const data = await res.json();
    return {
        ok: data?.status === "1" || data?.status === 1,
        data,
        message: data?.message || "",
    };
};
